import {UseQueryResult} from '@tanstack/react-query';
import {z} from 'zod';

import {SingleFilterRequestType} from '../../../page/main/reviews-analysis/reviews-analysis-type';
import {TableSorterHookReturnType} from '../../../util/antd/table/table-sorter-hook';
import {PaginationType} from '../../../util/pagination-hook/pagination-hook-type';
import {PaginatedResponseType} from '../../api/api-type';
import {ReviewsAnalysisModeEnum} from '../reivew-analysis-const';
import {ReviewRateEnum} from '../review-rate/review-rate-type';
import {sentimentDataSchema, SentimentEnum} from '../review-sentiment/review-sentiment-type';

const reviewAspectReviewsValueSchema = z.object({
    value: z.number(),
    comparedValue: z.number().nullish(),
    delta: z.number().nullish(),
});

const tonalityPeriodDataSchema = z.object({
    negative: z.number(),
    neutral: z.number(),
    positive: z.number(),
});

const reviewsPeriodDataSchema = z.object({
    reviewsCount: z.number(),
    tonalityIdx: z.number(),
    avgRating: z.number(),
});

export const reviewAspectRecordSchema = z.object({
    ids: z.array(z.number()),
    name: z.string(),
    negativeReviews: reviewAspectReviewsValueSchema,
    neutralReviews: reviewAspectReviewsValueSchema,
    positiveReviews: reviewAspectReviewsValueSchema,
    tonalityCurrentPeriodData: tonalityPeriodDataSchema,
    tonalityComparePeriodData: tonalityPeriodDataSchema.nullish(),
    reviewsCurrentPeriodData: reviewsPeriodDataSchema,
    reviewsComparePeriodData: reviewsPeriodDataSchema.nullish(),
    tonalityIdx: reviewAspectReviewsValueSchema,
    totalReviews: reviewAspectReviewsValueSchema,
});

export type ReviewAspectRecordType = z.infer<typeof reviewAspectRecordSchema>;

export enum ReviewAspectTableSortedColumnsEnum {
    mainNegativeReviews = 'negative_reviews',
    deltaNegativeReviews = 'negative_reviews__delta',
    compareNegativeReviews = 'negative_reviews__compared_value',

    mainNeutralReviews = 'neutral_reviews',
    deltaNeutralReviews = 'neutral_reviews__delta',
    compareNeutralReviews = 'neutral_reviews__compared_value',

    mainPositiveReviews = 'positive_reviews',
    deltaPositiveReviews = 'positive_reviews__delta',
    comparePositiveReviews = 'positive_reviews__compared_value',

    mainTonalityIndex = 'tonality_idx',
    deltaTonalityIndex = 'tonality_idx__delta',
    compareTonalityIndex = 'tonality_idx__compared_value',

    mainTotalReviews = 'total_reviews',
    deltaTotalReviews = 'total_reviews__delta',
    compareTotalReviews = 'total_reviews__compared_value',
}

export const reviewAspectsToneDynamicsSchema = z.object({
    labels: z.array(z.string()),
    aspectsData: z.array(
        z.object({
            aspect: z.string(),
            currentPeriodData: z.array(z.number()),
            comparePeriodData: z.array(z.number()).nullish(),
        })
    ),
});

export type ReviewAspectsToneType = z.infer<typeof reviewAspectsToneDynamicsSchema>;

export const reviewsCountByAspectsSchema = z.object({
    reviewsByAspects: z.array(
        z.object({
            aspect: z.string(),
            currentPeriodData: z.object({
                reviewsCount: z.number(),
                tonalityIdx: z.number(),
            }),
            comparePeriodData: z
                .object({
                    reviewsCount: z.number(),
                    tonalityIdx: z.number(),
                })
                .nullish(),
        })
    ),
});

export type ReviewsCountByAspectsType = z.infer<typeof reviewsCountByAspectsSchema>;

export type AspectsAnalysisRequestParametersType = SingleFilterRequestType & {
    comparedStartDate?: string | null;
    comparedEndDate?: string | null;
    filterKey: string;
};

export type AspectsAnalysisTableRequestParametersType = AspectsAnalysisRequestParametersType & {
    sortQueryValue: string | null;
};

export type ReviewAnalyticsAspectsHookType = UseQueryResult<PaginatedResponseType<ReviewAspectRecordType>> &
    PaginationType & {
        sorter: TableSorterHookReturnType;
    };

export const topAspectsItemSchema = z.object({
    aspect: z.string(),
    avgRating: z.number(),
    reviewsCount: z.number(),
    sentiment: sentimentDataSchema,
});

export type TopAspectsItemType = z.infer<typeof topAspectsItemSchema>;

export type TopAspectsQueryParametersType = {
    startDate: string | null;
    endDate: string | null;
    ableToReply: boolean | null;
    mode?: ReviewsAnalysisModeEnum;
    brandIds?: Array<number>;
    companyIds?: Array<number>;
};

export type ExportAspectsReportOptionsType = {
    mode?: ReviewsAnalysisModeEnum;
    brandIds?: Array<number>;
    startDate: Date | null;
    endDate: Date | null;
    sentiments: Array<SentimentEnum>;
    sources: Array<number>;
    tags: Array<string>;
    phrases: Array<string>;
    topics: Array<string>;
    userTagsIds: Array<number>;
    aspectIds: Array<number>;
    rating: Array<ReviewRateEnum>;
    withAnswers: boolean | null;
    target: string | null;
    useDeleted: boolean | null;
    comparedStartDate?: Date | null;
    comparedEndDate?: Date | null;
};
